import React, { useEffect, useMemo, useState } from "react";
import { Button, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { InfoCircleOutlined } from "@ant-design/icons";
import { statusTone } from "../../shared/statusColors";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  InternalTaskGroup,
  InternalTaskRow,
  PLANNED_RATE_FORMULA,
  EXECUTED_RATE_FORMULA,
  EXECUTED_PLANNED_RATE_FORMULA,
  internalTaskGroupKey,
  fmtINR,
  fmtQty,
  num,
} from "./internalTaskShared";

// Internal-task-number register as a 3-level tree: bold group rows (one per
// internal number + UOM — mixed-UOM numbers arrive as multiple groups,
// rendered as-is) → activity rows (built CLIENT-SIDE by bucketing the group's
// tasks on activityId, with qty/amount sums and weighted rates recomputed per
// activity) → task rows. Everything is client-side — the page hands in the
// (search-filtered) groups and antd paginates the top-level rows;
// expand/collapse is controlled so Expand all / Collapse all can drive both
// levels. Keys are content-based (number + UOM + activity id), so expanded
// rows stay expanded while the user types in the page search box.

interface TaskChildRow extends InternalTaskRow {
  key: string;
  rowType: "task";
  qtyNum: number;
  plannedRateNum: number;
  plannedAmountNum: number;
  executedRateNum: number;
  executedAmountNum: number;
  executedPlannedRateNum: number;
  executedPlannedAmountNum: number;
}

interface ActivityRow {
  key: string;
  rowType: "activity";
  activityName: string;
  uomName: string;
  taskCount: number;
  qtyNum: number;
  plannedRateNum: number;
  plannedAmountNum: number;
  executedRateNum: number;
  executedAmountNum: number;
  executedPlannedRateNum: number;
  executedPlannedAmountNum: number;
  children: TaskChildRow[];
}

interface GroupRow {
  key: string;
  rowType: "group";
  internalTaskNumber: string;
  isUnassigned: boolean;
  uomName: string;
  taskCount: number;
  qtyNum: number;
  plannedRateNum: number;
  plannedAmountNum: number;
  executedRateNum: number;
  executedAmountNum: number;
  executedPlannedRateNum: number;
  executedPlannedAmountNum: number;
  children: ActivityRow[];
  /** 'B3 — Underground Excavation'; '' when the number resolves to no group. */
  normGroupLabel: string;
  /** The BOQ's own description for this code, e.g. 'Class I, II, & III'. */
  normTitle: string;
}

// Norm-group level above the internal task numbers. The number IS a BOQ code,
// so 95/95 of them resolve; blank numbers fall into a trailing "Unassigned"
// bucket exactly as before.
//
// ₹ TOTALS ONLY on this row: a norm group spans several UOMs (B1 is in KWh,
// B11 in Metric Ton, B3 in Cubic Meter), so quantity and the three weighted
// rates cannot be rolled up across it and are left blank unless every child
// shares one UOM. Same rule as the 3M Deviation Summary register.
interface NormGroupRow {
  key: string;
  rowType: "normGroup";
  groupLabel: string;
  numberCount: number;
  taskCount: number;
  uomName: string;
  qtyNum: number | null;
  plannedAmountNum: number;
  executedAmountNum: number;
  executedPlannedAmountNum: number;
  children: GroupRow[];
}

type TableRow = TaskChildRow | ActivityRow | GroupRow | NormGroupRow;

const NORM_GROUP_ROW_BG = "#eef3fa";

// A quantity or weighted rate cannot be rolled up across a norm group whose
// children use different units (B1 is KWh, B11 Metric Ton, B3 Cubic Meter), so
// those cells explain themselves instead of showing an invented number. The ₹
// columns still total — money is additive across units.
const mixedUomDash = (row: { uomName?: string }) => (
  <Tooltip
    title={
      row.uomName
        ? "Rolled up across this group"
        : "Items in this group use different units, so a per-unit figure cannot be rolled up. The ₹ columns still total."
    }
  >
    <span style={{ color: "#c9d2e3" }}>—</span>
  </Tooltip>
);
const GROUP_ROW_BG = "#f5f7fa";
const ACTIVITY_ROW_BG = "#fbfcfd";
const INK = "#022c57";
const ACTIVITY_INK = "#434343";
const MONO_FONT = "SFMono-Regular, Consolas, Menlo, monospace";

// Column header for the two weighted-rate columns: each carries its OWN
// formula text (planned is qty-weighted; executed derives from 3M cost) as a
// Tooltip behind an info suffix.
const rateHeader = (label: string, formula: string) => (
  <Tooltip title={formula}>
    <span style={{ cursor: "help" }}>
      {label}{" "}
      <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 11 }} />
    </span>
  </Tooltip>
);

const groupNum = (value: number) => (
  <span
    style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: INK }}
  >
    {fmtINR(value)}
  </span>
);

const activityNum = (value: number) => (
  <span
    style={{
      fontVariantNumeric: "tabular-nums",
      fontWeight: 600,
      color: ACTIVITY_INK,
    }}
  >
    {fmtINR(value)}
  </span>
);

const childNum = (value: number) => (
  <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(value)}</span>
);

// Bucket a group's tasks by activity, recomputing the same aggregates the
// backend sends at group level: Σqty, Σ(qty×rate) (= Σ amounts, since each
// task's amount is already qty×rate) and weighted rate = Σamount ÷ Σqty.
const buildActivityRows = (
  groupKey: string,
  tasks: TaskChildRow[]
): ActivityRow[] => {
  const buckets = new Map<string, ActivityRow>();
  tasks.forEach((task) => {
    const id =
      task.activityId != null && task.activityId !== 0
        ? `ID-${task.activityId}`
        : `NAME-${task.activityName || ""}`;
    let bucket = buckets.get(id);
    if (!bucket) {
      bucket = {
        key: `${groupKey}-ACT-${id}`,
        rowType: "activity",
        activityName: task.activityName || "—",
        uomName: task.uomName || "",
        taskCount: 0,
        qtyNum: 0,
        plannedRateNum: 0,
        plannedAmountNum: 0,
        executedRateNum: 0,
        executedAmountNum: 0,
        executedPlannedRateNum: 0,
        executedPlannedAmountNum: 0,
        children: [],
      };
      buckets.set(id, bucket);
    }
    bucket.taskCount += 1;
    bucket.qtyNum += task.qtyNum;
    bucket.plannedAmountNum += task.plannedAmountNum;
    bucket.executedAmountNum += task.executedAmountNum;
    bucket.executedPlannedAmountNum += task.executedPlannedAmountNum;
    bucket.children.push(task);
  });
  const rows = Array.from(buckets.values());
  rows.forEach((row) => {
    row.plannedRateNum = row.qtyNum > 0 ? row.plannedAmountNum / row.qtyNum : 0;
    row.executedRateNum =
      row.qtyNum > 0 ? row.executedAmountNum / row.qtyNum : 0;
    row.executedPlannedRateNum =
      row.qtyNum > 0 ? row.executedPlannedAmountNum / row.qtyNum : 0;
  });
  return rows.sort((a, b) => {
    if (a.activityName === "—") return 1;
    if (b.activityName === "—") return -1;
    return a.activityName.localeCompare(b.activityName);
  });
};

const InternalTaskSummaryTable: React.FC<{
  /** Already search-filtered by the page. */
  groups: InternalTaskGroup[];
  loading?: boolean;
}> = ({ groups, loading }) => {
  const wbsName = useWbsName();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const groupRows = useMemo<GroupRow[]>(
    () =>
      groups.map((group) => {
        const groupKey = internalTaskGroupKey(group);
        const tasks: TaskChildRow[] = (group.tasks || []).map((task) => ({
          ...task,
          key: `${groupKey}-TASK-${task.taskId ?? 0}`,
          rowType: "task" as const,
          qtyNum: num(task.workQuantity),
          plannedRateNum: num(task.rate),
          plannedAmountNum: num(task.plannedAmount),
          executedRateNum: num(task.executedRate),
          executedAmountNum: num(task.executedAmount),
          executedPlannedRateNum: num(task.executedPlannedRate),
          executedPlannedAmountNum: num(task.executedPlannedAmount),
        }));
        return {
          key: groupKey,
          rowType: "group" as const,
          internalTaskNumber: group.internalTaskNumber || "—",
          isUnassigned: !!group.isUnassigned,
          uomName: group.uomName || "",
          taskCount: num(group.taskCount) || tasks.length,
          qtyNum: num(group.totalQuantity),
          plannedRateNum: num(group.weightedPlannedRate),
          plannedAmountNum: num(group.plannedAmount),
          executedRateNum: num(group.weightedExecutedRate),
          executedAmountNum: num(group.executedAmount),
          executedPlannedRateNum: num(group.weightedExecutedPlannedRate),
          executedPlannedAmountNum: num(group.executedPlannedAmount),
          children: buildActivityRows(groupKey, tasks),
          normGroupLabel: group.level1Code
            ? `${group.level1Code} — ${group.level1Name || ""}`.replace(/ — $/, "")
            : "",
          normTitle: group.normTitle || "",
        };
      }),
    [groups]
  );

  // Bucket the number rows under their norm group, preserving the server's
  // order (already numeric: B9 before B10) and keeping Unassigned last.
  const rows = useMemo<NormGroupRow[]>(() => {
    const buckets = new Map<string, GroupRow[]>();
    groupRows.forEach((row) => {
      const label = row.normGroupLabel || "Unassigned";
      if (!buckets.has(label)) buckets.set(label, []);
      buckets.get(label)!.push(row);
    });
    return Array.from(buckets.entries())
      .map(([label, children]) => {
        const uoms = new Set(children.map((c) => c.uomName).filter(Boolean));
        const oneUom = uoms.size === 1;
        return {
          key: `NORMGROUP-${label}`,
          rowType: "normGroup" as const,
          groupLabel: label,
          numberCount: children.length,
          taskCount: children.reduce((n, c) => n + c.taskCount, 0),
          uomName: oneUom ? Array.from(uoms)[0] : "",
          qtyNum: oneUom ? children.reduce((n, c) => n + c.qtyNum, 0) : null,
          plannedAmountNum: children.reduce((n, c) => n + c.plannedAmountNum, 0),
          executedAmountNum: children.reduce((n, c) => n + c.executedAmountNum, 0),
          executedPlannedAmountNum: children.reduce(
            (n, c) => n + c.executedPlannedAmountNum,
            0
          ),
          children,
        };
      })
      .sort((a, b) => {
        // Unassigned always trails, regardless of value.
        const aU = a.groupLabel === "Unassigned" ? 1 : 0;
        const bU = b.groupLabel === "Unassigned" ? 1 : 0;
        if (aU !== bU) return aU - bU;
        return a.groupLabel.localeCompare(b.groupLabel, undefined, {
          numeric: true,
        });
      });
  }, [groupRows]);

  // Collapse whenever the dataset changes (phase/activity filter, refresh) —
  // not just on mount, or stale expansions survive a filter change.
  useEffect(() => {
    setExpandedRowKeys([]);
  }, [groups]);

  const allExpandableKeys = useMemo<React.Key[]>(
    () =>
      rows.flatMap((row) => [
        row.key,
        ...row.children.flatMap((group) => [
          group.key,
          ...group.children.map((activity) => activity.key),
        ]),
      ]),
    [rows]
  );

  const columns: ColumnsType<TableRow> = [
    {
      title: "Internal Task No.",
      key: "internalTaskNumber",
      render: (_: any, row) => {
        if (row.rowType === "normGroup") {
          return (
            <span>
              <b style={{ color: INK }}>{row.groupLabel}</b>
              <span
                style={{ fontSize: 11.5, color: "#8094ae", marginLeft: 8 }}
              >
                {row.numberCount} number{row.numberCount === 1 ? "" : "s"} ·{" "}
                {row.taskCount} task{row.taskCount === 1 ? "" : "s"}
              </span>
            </span>
          );
        }
        if (row.rowType === "group") {
          return (
            <span style={{ fontWeight: 600, color: INK }}>
              {row.internalTaskNumber}
              {row.normTitle ? (
                <span
                  style={{ fontWeight: 400, color: "#526484", marginLeft: 8 }}
                  title={row.normTitle}
                >
                  {row.normTitle}
                </span>
              ) : null}
              {row.isUnassigned ? (
                <Tag
                  style={{
                    marginLeft: 8,
                    marginRight: 0,
                    fontSize: 11,
                    color: "#8c8c8c",
                    background: "#fafafa",
                    borderColor: "#d9d9d9",
                  }}
                >
                  Unassigned
                </Tag>
              ) : null}
            </span>
          );
        }
        if (row.rowType === "activity") {
          return (
            <span
              style={{ fontWeight: 600, color: ACTIVITY_INK }}
              title={row.activityName}
            >
              {wbsName(row.activityName)}
            </span>
          );
        }
        return (
          <span>
            <span
              style={{ fontFamily: MONO_FONT, fontSize: 12, color: "#595959" }}
            >
              {row.taskSequenceId || "—"}
            </span>
            <span style={{ color: "#8c8c8c" }}> — </span>
            <span>{row.taskName || "—"}</span>
          </span>
        );
      },
    },
    {
      title: "UOM",
      key: "uomName",
      width: 90,
      render: (_: any, row) =>
        row.rowType === "normGroup" ? (
          <span style={{ fontWeight: 600, color: row.uomName ? INK : "#c9d2e3" }}>
            {row.uomName || <span style={{ fontStyle: "italic" }}>mixed</span>}
          </span>
        ) : row.rowType === "group" ? (
          <span style={{ fontWeight: 600, color: INK }}>
            {row.uomName || "—"}
          </span>
        ) : (
          row.uomName || "—"
        ),
    },
    {
      title: "Total Qty",
      key: "qty",
      width: 120,
      align: "right",
      render: (_: any, row) =>
        // null on a mixed-UOM norm group: summing Cubic Meter with KWh would
        // be a fake number, and fmtQty(null) would print a fake 0.00.
        row.rowType === "normGroup" && row.qtyNum === null ? (
          mixedUomDash(row)
        ) : (
          <span
            style={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: row.rowType === "task" ? undefined : 600,
              color:
                row.rowType === "group"
                  ? INK
                  : row.rowType === "activity"
                  ? ACTIVITY_INK
                  : undefined,
            }}
          >
            {fmtQty(row.qtyNum)}
          </span>
        ),
    },
    {
      title: rateHeader("Wtd Avg Rate (Planned)", PLANNED_RATE_FORMULA),
      key: "plannedRate",
      width: 160,
      align: "right",
      render: (_: any, row) =>
        row.rowType === "normGroup"
          ? mixedUomDash(row)
          : row.rowType === "group"
          ? groupNum(row.plannedRateNum)
          : row.rowType === "activity"
          ? activityNum(row.plannedRateNum)
          : childNum(row.plannedRateNum),
    },
    {
      title: "Planned Amount",
      key: "plannedAmount",
      width: 150,
      align: "right",
      render: (_: any, row) =>
        row.rowType === "normGroup"
          ? groupNum(row.plannedAmountNum)
          : row.rowType === "group"
          ? groupNum(row.plannedAmountNum)
          : row.rowType === "activity"
          ? activityNum(row.plannedAmountNum)
          : childNum(row.plannedAmountNum),
    },
    {
      title: rateHeader(
        "Wtd Avg Rate (Executed-Planned)",
        EXECUTED_PLANNED_RATE_FORMULA
      ),
      key: "executedPlannedRate",
      width: 170,
      align: "right",
      render: (_: any, row) =>
        row.rowType === "normGroup"
          ? mixedUomDash(row)
          : row.rowType === "group"
          ? groupNum(row.executedPlannedRateNum)
          : row.rowType === "activity"
          ? activityNum(row.executedPlannedRateNum)
          : childNum(row.executedPlannedRateNum),
    },
    {
      title: "Executed-Planned Amount",
      key: "executedPlannedAmount",
      width: 150,
      align: "right",
      render: (_: any, row) =>
        row.rowType === "normGroup"
          ? groupNum(row.executedPlannedAmountNum)
          : row.rowType === "group"
          ? groupNum(row.executedPlannedAmountNum)
          : row.rowType === "activity"
          ? activityNum(row.executedPlannedAmountNum)
          : childNum(row.executedPlannedAmountNum),
    },
    {
      title: rateHeader("Wtd Avg Rate (Executed)", EXECUTED_RATE_FORMULA),
      key: "executedRate",
      width: 160,
      align: "right",
      render: (_: any, row) =>
        row.rowType === "normGroup"
          ? mixedUomDash(row)
          : row.rowType === "group"
          ? groupNum(row.executedRateNum)
          : row.rowType === "activity"
          ? activityNum(row.executedRateNum)
          : childNum(row.executedRateNum),
    },
    {
      title: "Executed Amount",
      key: "executedAmount",
      width: 150,
      align: "right",
      render: (_: any, row) =>
        row.rowType === "normGroup"
          ? groupNum(row.executedAmountNum)
          : row.rowType === "group"
          ? groupNum(row.executedAmountNum)
          : row.rowType === "activity"
          ? activityNum(row.executedAmountNum)
          : childNum(row.executedAmountNum),
    },
    {
      title: "Tasks",
      key: "tasks",
      width: 120,
      align: "right",
      render: (_: any, row) => {
        if (row.rowType !== "task") {
          return (
            <span
              style={{
                fontVariantNumeric: "tabular-nums",
                fontWeight: 600,
                color: row.rowType === "group" ? INK : ACTIVITY_INK,
              }}
            >
              {row.taskCount}
            </span>
          );
        }
        const tone = statusTone(row.taskStatus);
        return (
          <Tag
            style={{
              marginRight: 0,
              fontSize: 11,
              color: tone.text,
              background: tone.bg,
              borderColor: "transparent",
            }}
          >
            {row.taskStatus || "—"}
          </Tag>
        );
      },
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          marginBottom: 8,
        }}
      >
        <span style={{ color: "#8c8c8c", fontSize: 13 }}>
          {rows.length} internal task number{rows.length === 1 ? "" : "s"}
        </span>
        <div style={{ marginLeft: "auto" }}>
          <Button
            type="link"
            size="small"
            disabled={rows.length === 0}
            onClick={() => setExpandedRowKeys(allExpandableKeys)}
          >
            Expand all
          </Button>
          <Button
            type="link"
            size="small"
            disabled={expandedRowKeys.length === 0}
            onClick={() => setExpandedRowKeys([])}
          >
            Collapse all
          </Button>
        </div>
      </div>
      <Table<TableRow>
        size="small"
        columns={columns}
        dataSource={rows}
        loading={loading}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
        }}
        onRow={(row) =>
          row.rowType === "normGroup"
            ? { style: { background: NORM_GROUP_ROW_BG } }
            : row.rowType === "group"
            ? { style: { background: GROUP_ROW_BG } }
            : row.rowType === "activity"
            ? { style: { background: ACTIVITY_ROW_BG } }
            : {}
        }
        pagination={{
          defaultPageSize: 25,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} norm groups`,
        }}
        scroll={{ x: 1470 }}
      />
    </>
  );
};

export default InternalTaskSummaryTable;
